/* ────────────────────────────────────────────────────────────
 * overlayStack.ts — Centralized overlay lifecycle manager
 *
 * Solves:
 * 1. ESC Cascade: Only the top-most overlay closes on Escape.
 * 2. Body Scroll Locking: Maintains body overflow lock as long as
 *    at least one overlay is active.
 * 3. Safe Stacking: Avoids raw window.history manipulation.
 * ──────────────────────────────────────────────────────────── */

interface OverlayEntry {
  id: string;
  onClose: () => void;
}

let stack: OverlayEntry[] = [];
let nextId = 0;
let isListenerAttached = false;

// ── Keyboard (Escape) handler ──
function handleKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && stack.length > 0) {
    e.preventDefault();
    e.stopImmediatePropagation();
    const top = stack[stack.length - 1];
    top.onClose();
  }
}

function updateBodyScroll() {
  if (stack.length > 0) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
}

function attachListeners() {
  if (!isListenerAttached) {
    window.addEventListener('keydown', handleKeyDown, true);
    isListenerAttached = true;
  }
}

function detachListeners() {
  if (stack.length === 0 && isListenerAttached) {
    window.removeEventListener('keydown', handleKeyDown, true);
    isListenerAttached = false;
  }
}

import { useSyncExternalStore } from 'react';

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      console.error(e);
    }
  });
}

export const overlayStack = {
  /**
   * Register an overlay. Call when the overlay opens.
   * Returns a unique ID to use when removing.
   */
  push(onClose: () => void): string {
    const id = `overlay-${++nextId}`;
    stack.push({ id, onClose });
    attachListeners();
    updateBodyScroll();
    notify();
    return id;
  },

  /**
   * Unregister an overlay. Call when the overlay closes.
   */
  remove(id: string): void {
    const idx = stack.findIndex((entry) => entry.id === id);
    if (idx !== -1) {
      stack.splice(idx, 1);
    }
    if (stack.length === 0) {
      detachListeners();
    }
    updateBodyScroll();
    notify();
  },

  /**
   * Subscribe to overlay stack depth changes.
   */
  subscribe(callback: Listener): () => void {
    listeners.add(callback);
    return () => {
      listeners.delete(callback);
    };
  },

  /**
   * Check if a given overlay is the top-most (for conditional rendering).
   */
  isTop(id: string): boolean {
    return stack.length > 0 && stack[stack.length - 1].id === id;
  },

  /**
   * Check if a given overlay is the bottom-most (first opened).
   */
  isBottomMost(id: string): boolean {
    return stack.length > 0 && stack[0].id === id;
  },

  /**
   * Get current stack depth.
   */
  get depth(): number {
    return stack.length;
  },
};

/**
 * React hook to observe active overlay depth with useSyncExternalStore
 */
export function useOverlayDepth(): number {
  return useSyncExternalStore(
    (callback) => overlayStack.subscribe(callback),
    () => overlayStack.depth,
    () => 0
  );
}
